import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'herond-theme-mode';

const getSystemScheme = (): ColorScheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const getStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch { /* ignore */ }
  return 'system';
};

export const useThemeMode = () => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(getSystemScheme);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemScheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const colorScheme: ColorScheme = mode === 'system' ? systemScheme : mode;

  const setMode = (newMode: ThemeMode) => {
    try { localStorage.setItem(STORAGE_KEY, newMode); } catch { /* ignore */ }
    setModeState(newMode);
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', colorScheme === 'dark');
  }, [colorScheme]);

  return { mode, setMode, colorScheme };
};
